import React, { useState, useEffect } from 'react';
import { contactAPI } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Mail, Phone, Building2, Check, Eye } from 'lucide-react';
import { formatDateTime } from '../../lib/utils';

const ContactsPage = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const response = await contactAPI.getAll();
      setContacts(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkProcessed = async (id) => {
    try {
      await contactAPI.markProcessed(id);
      setContacts(contacts.map((c) =>
        c.id === id ? { ...c, traite: true } : c
      ));
      toast.success('Marqué comme traité');
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleViewContact = (contact) => {
    setSelectedContact(contact);
    setDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  const unprocessedCount = contacts.filter((c) => !c.traite).length;

  return (
    <div className="space-y-6" data-testid="contacts-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">Demandes de contact</h1>
        <p className="text-slate-600">
          {unprocessedCount > 0 ? `${unprocessedCount} demande(s) non traitée(s)` : 'Toutes les demandes sont traitées'}
        </p>
      </div>

      <Card>
        <CardContent className="p-0">
          {contacts.length === 0 ? (
            <div className="py-12 text-center">
              <Mail className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">Aucune demande de contact.</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {contacts.map((contact) => (
                <div
                  key={contact.id}
                  className={`flex items-start gap-4 p-4 hover:bg-slate-50 transition-colors ${
                    !contact.traite ? 'bg-primary/5' : ''
                  }`}
                >
                  <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{contact.nom}</span>
                      {!contact.traite && (
                        <Badge variant="default" className="bg-amber-100 text-amber-800">
                          Non traité
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-600">{contact.email}</p>
                    {contact.entreprise && (
                      <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                        <Building2 className="w-3 h-3" />
                        {contact.entreprise}
                      </p>
                    )}
                    <p className="text-xs text-slate-400 mt-2">
                      {formatDateTime(contact.created_at)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleViewContact(contact)}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    {!contact.traite && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleMarkProcessed(contact.id)}
                        className="text-green-600 hover:text-green-700"
                      >
                        <Check className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Contact Detail Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Détails de la demande</DialogTitle>
          </DialogHeader>
          {selectedContact && (
            <div className="space-y-4">
              <div>
                <p className="text-sm text-slate-500">Nom</p>
                <p className="font-medium">{selectedContact.nom}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Courriel</p>
                <a href={`mailto:${selectedContact.email}`} className="text-primary hover:underline">
                  {selectedContact.email}
                </a>
              </div>
              {selectedContact.telephone && (
                <div>
                  <p className="text-sm text-slate-500">Téléphone</p>
                  <a href={`tel:${selectedContact.telephone}`} className="text-primary hover:underline">
                    {selectedContact.telephone}
                  </a>
                </div>
              )}
              {selectedContact.entreprise && (
                <div>
                  <p className="text-sm text-slate-500">Entreprise</p>
                  <p>{selectedContact.entreprise}</p>
                </div>
              )}
              <div>
                <p className="text-sm text-slate-500">Message</p>
                <p className="bg-slate-50 p-4 rounded-lg mt-1 whitespace-pre-wrap">
                  {selectedContact.message}
                </p>
              </div>
              <div className="text-xs text-slate-400">
                Reçu le {formatDateTime(selectedContact.created_at)}
              </div>
              {!selectedContact.traite && (
                <Button
                  onClick={() => {
                    handleMarkProcessed(selectedContact.id);
                    setDialogOpen(false);
                  }}
                  className="w-full"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Marquer comme traité
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContactsPage;
